import { DragStart, DragUpdate } from '@hello-pangea/dnd';
import { useCallback, useState } from 'react';

export type DragIndicatorStyle = {
  top: number;
  width: number;
  height: number;
};

export type IndicatorEntry = {
  groupId: string;
  style: DragIndicatorStyle;
} | null;

export const DRAGGABLE_ATTR = 'data-rfd-draggable-id';
export const DROPPABLE_ATTR = 'data-rfd-droppable-id';
export const DROP_OFFSET_ATTR = 'data-drop-offset';

export const getDraggedDom = (draggableId: string) =>
  document.querySelector<HTMLElement>(`[${DRAGGABLE_ATTR}='${draggableId}']`);

const getDroppableDom = (droppableId: string) =>
  document.querySelector<HTMLElement>(`[${DROPPABLE_ATTR}='${droppableId}']`);

const getChildrenHeightSum = (children: HTMLElement[], count: number) =>
  children.slice(0, count).reduce((total, curr) => {
    const style = window.getComputedStyle(curr);
    const marginBottom = parseFloat(style.marginBottom);
    const borderWidth = parseFloat(style.borderWidth);
    return total + curr.clientHeight + marginBottom + borderWidth * 2;
  }, 0);

const computeIndicatorEntry = (
  droppableId: string,
  droppableDOM: HTMLElement,
  draggedDOM: HTMLElement,
  children: HTMLElement[],
  targetIndex: number,
): IndicatorEntry => {
  const paddingTop = parseFloat(
    window.getComputedStyle(droppableDOM).paddingTop,
  );

  const childrenWithoutDragged = children.filter(child => child !== draggedDOM);

  const top =
    paddingTop + getChildrenHeightSum(childrenWithoutDragged, targetIndex);

  return {
    groupId: droppableId,
    style: {
      height: draggedDOM.clientHeight,
      width: draggedDOM.clientWidth,
      top,
    },
  };
};

const useDragIndicator = (enabled = true) => {
  const [indicator, setIndicator] = useState<IndicatorEntry>(null);

  const onDragStart = useCallback(
    (start: DragStart) => {
      if (!enabled) return;

      const draggedDOM = getDraggedDom(start.draggableId);
      if (!draggedDOM) return;

      const droppableDOM = getDroppableDom(start.source.droppableId);
      if (!droppableDOM) return;

      const children = [...droppableDOM.children] as HTMLElement[];

      setIndicator(
        computeIndicatorEntry(
          start.source.droppableId,
          droppableDOM,
          draggedDOM,
          children,
          start.source.index,
        ),
      );
    },
    [enabled],
  );

  const onDragUpdate = useCallback(
    (update: DragUpdate) => {
      if (!enabled) return;

      if (!update.destination) {
        setIndicator(null);
        return;
      }

      const draggedDOM = getDraggedDom(update.draggableId);
      if (!draggedDOM) return;

      const destinationDroppableId = update.destination.droppableId;
      const droppableDOM = getDroppableDom(destinationDroppableId);
      if (!droppableDOM) return;

      const destinationIndex = update.destination.index;
      const children = [...droppableDOM.children] as HTMLElement[];

      setIndicator(
        computeIndicatorEntry(
          destinationDroppableId,
          droppableDOM,
          draggedDOM,
          children,
          destinationIndex,
        ),
      );
    },
    [enabled],
  );

  const onDragEnd = useCallback(() => {
    if (enabled) setIndicator(null);
  }, [enabled]);

  return { indicator, onDragStart, onDragUpdate, onDragEnd };
};

export default useDragIndicator;
